import React, {
  createContext,
  useContext,
  useMemo,
  useState,
} from 'react';
import { node, oneOf } from 'prop-types';

import { ExperienceContext } from '@thd-nucleus/experience-context';

import { usePromoCart } from '../hooks/usePromoCart';
import { usePromoModel } from '../hooks/usePromoModel';
import { getPreviewImages, isAppleTouch } from '../utils/promo-presentation-utils';
import { DOLLAR_THRESHOLD_BOGO_SUBEXPERIENCE_TAGS, FORWARDS_BOGO_SUBEXPERIENCE_TAGS } from '../utils/constants';

export const defaultPresentationContext = {
  firstListPreviewImages: [],
  secondListPreviewImages: [],
  isAppleTouchDevice: false,
  isDrawerOpen: false,
  isRewardEnabled: false,
};

export const PromoPresentationContext = createContext(defaultPresentationContext);

export const PromoPresentationProvider = ({
  children,
  type
}) => {
  const { channel } = useContext(ExperienceContext);
  const {
    allListProductsAndAnchor,
    displayableFirstListItemIds,
    displayableSecondListItemIds,
    isBmsm,
    isMultiSelect,
    rewardTiers,
    src1EligibilityCriteria,
    subExperience,
  } = usePromoModel();
  const {
    currentPromoTier,
    firstListCartQuantity,
    promoCartValue,
    selectedItemsModel,
  } = usePromoCart();

  const {
    minPurchaseAmount: tieredMinPurchaseAmount,
  } = rewardTiers[currentPromoTier];

  let isRewardEnabled = false;
  switch (subExperience) {
  case FORWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetY:
  case FORWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetYDollarOff:
  case FORWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetYPercentageOff: {
    isRewardEnabled = firstListCartQuantity >= src1EligibilityCriteria.minPurchaseQuantity;
    break;
  }
  case DOLLAR_THRESHOLD_BOGO_SUBEXPERIENCE_TAGS.buyMinAmountGetOne:
  case DOLLAR_THRESHOLD_BOGO_SUBEXPERIENCE_TAGS.buyMinAmountGetDollarOff:
  case DOLLAR_THRESHOLD_BOGO_SUBEXPERIENCE_TAGS.buyMinAmountGetPercentageOff: {
    isRewardEnabled = src1EligibilityCriteria.minPurchaseAmount > 0
      && promoCartValue >= src1EligibilityCriteria.minPurchaseAmount;
    break;
  }
  default: {
    isRewardEnabled = tieredMinPurchaseAmount > 0 && promoCartValue >= tieredMinPurchaseAmount;
    break;
  }
  }

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const isAppleTouchDevice = isAppleTouch();

  const showWarning = isBmsm && isMultiSelect && selectedItemsModel?.length > 0;

  const contextValue = useMemo(() => {
    const firstListPreviewImages = getPreviewImages({
      allListProductsAndAnchor,
      channel,
      itemIds: displayableFirstListItemIds,
    });
    const secondListPreviewImages = getPreviewImages({
      allListProductsAndAnchor,
      channel,
      itemIds: displayableSecondListItemIds,
    });
    return {
      firstListPreviewImages,
      secondListPreviewImages,
      isAppleTouchDevice,
      isDrawerOpen,
      isRewardEnabled,
      setIsDrawerOpen,
      showWarning,
      type,
    };
  }, [
    allListProductsAndAnchor,
    channel,
    displayableFirstListItemIds,
    displayableSecondListItemIds,
    isAppleTouchDevice,
    isDrawerOpen,
    isRewardEnabled,
    showWarning,
    type
  ]);

  return (
    <PromoPresentationContext.Provider value={contextValue}>
      { children }
    </PromoPresentationContext.Provider>
  );
};

PromoPresentationProvider.propTypes = {
  children: node.isRequired,
  type: oneOf(['card', 'pod']).isRequired
};
